import axios from "axios";
import { CommentType } from "types/comment.type";
import { TokenTradeType, TokenType, LatestTradeType } from "types/token.type";
import { userType } from "types/user.type";

axios.defaults.baseURL = import.meta.env.VITE_BACKEND_URL;
axios.defaults.headers.post["Content-Type"] = "application/json";

const toNumber = (value: unknown, fallback = 0): number => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

const mapCoinResponse = (coin: any): TokenType => {
  const tokenSupply = toNumber(coin?.tokenSupply ?? coin?.totalSupply);
  const tokenReserves = toNumber(
    coin?.tokenReserves ?? coin?.virtualTokenReserves ?? coin?.rReserveToken
  );
  const ethReserves = toNumber(
    coin?.ethReserves ?? coin?.lamportReserves ?? coin?.virtualEthReserves
  );
  const impliedEthPrice = toNumber(
    coin?.impliedEthPrice ?? (tokenReserves > 0 ? ethReserves / tokenReserves : 0)
  );
  const ethPriceUsd = toNumber(
    coin?.ethPriceUsd ?? coin?.originTokenPrice ?? coin?.pricePerEth
  );
  const impliedUsdPrice = toNumber(
    coin?.impliedUsdPrice ??
    (impliedEthPrice > 0 && ethPriceUsd > 0 ? impliedEthPrice * ethPriceUsd : 0)
  );
  const progressMcap = toNumber(
    coin?.progressMcap ?? tokenSupply * impliedEthPrice
  );
  const marketCapEth = toNumber(coin?.marketCapEth ?? progressMcap);
  const marketCapUsd = toNumber(
    coin?.marketCapUsd ?? marketCapEth * ethPriceUsd
  );
  const normalizedMarketCapEth = toNumber(coin?.normalizedMarketCapEth);
  const normalizedMarketCapUsd = toNumber(
    coin?.normalizedMarketCapUsd ?? normalizedMarketCapEth * ethPriceUsd
  );
  const targetMarketCapEth = toNumber(coin?.targetMarketCapEth);
  const targetMarketCapUsd = toNumber(
    coin?.targetMarketCapUsd ?? targetMarketCapEth * ethPriceUsd
  );

  return {
    _id: coin?._id ?? coin?.id ?? undefined,
    creator: coin?.creator ?? "",
    name: coin?.name ?? "",
    ticker: coin?.ticker ?? "",
    description: coin?.description ?? "",
    url: coin?.url ?? "",
    token: coin?.token ?? coin?.tokenAddress ?? coin?.mint ?? "",
    twitter: coin?.twitter ?? "",
    telegram: coin?.telegram ?? "",
    website: coin?.website ?? "",
    tokenSupply,
    tokenReserves,
    lamportReserves: toNumber(coin?.lamportReserves),
    ethReserves,
    impliedEthPrice,
    impliedUsdPrice,
    progressMcap,
    marketCapEth,
    marketCapUsd,
    normalizedMarketCapEth,
    normalizedMarketCapUsd,
    initialVirtualEth: toNumber(coin?.initialVirtualEth ?? coin?.initialVirtualEthWei),
    ethRaised: toNumber(coin?.ethRaised),
    targetRaiseEth: toNumber(coin?.targetRaiseEth),
    targetMarketCapEth,
    targetMarketCapUsd,
    bondingCurve: Boolean(coin?.bondingCurve),
    graduatedPoolId: coin?.graduatedPoolId ?? "",
    graduatedPairAddress: coin?.graduatedPairAddress ?? "",
    graduatedPoolKey: coin?.graduatedPoolKey
      ? {
        currency0: coin?.graduatedPoolKey?.currency0 ?? "",
        currency1: coin?.graduatedPoolKey?.currency1 ?? "",
        fee: toNumber(coin?.graduatedPoolKey?.fee),
        tickSpacing: toNumber(coin?.graduatedPoolKey?.tickSpacing),
        hooks: coin?.graduatedPoolKey?.hooks ?? "",
      }
      : undefined,
    graduatedChainId: typeof coin?.graduatedChainId === "number" ? coin?.graduatedChainId : null,
    graduatedBlockNumber: typeof coin?.graduatedBlockNumber === "number" ? coin?.graduatedBlockNumber : null,
    graduatedTxHash: coin?.graduatedTxHash ?? "",
    graduatedAt: coin?.graduatedAt ?? undefined,
    reply: toNumber(coin?.reply),
    createdAt: coin?.createdAt ?? coin?.date ?? undefined,
    updatedAt: coin?.updatedAt ?? undefined,
  };
};

const normalizeTimestamp = (value: unknown): string => {
  if (typeof value === "number") {
    return new Date(value).toISOString();
  }
  if (typeof value === "string" && value.length > 0) {
    return value;
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  return new Date().toISOString();
};

const mapTradeRecord = (trade: any): TokenTradeType => {
  const timestamp = normalizeTimestamp(
    trade?.timestamp ?? trade?.time ?? trade?.createdAt
  );
  const tokenAmount = toNumber(trade?.tokenAmount ?? trade?.token_amount);
  const ethAmount = toNumber(
    trade?.ethAmount ?? trade?.lamportAmount ?? trade?.eth_amount
  );
  const impliedEthPrice = toNumber(
    trade?.impliedEthPrice ?? (tokenAmount > 0 ? ethAmount / tokenAmount : 0)
  );
  const ethPriceUsd = toNumber(trade?.ethPriceUsd ?? trade?.originTokenPrice ?? 0);
  const impliedUsdPrice = toNumber(
    trade?.impliedUsdPrice ?? trade?.price ??
    (impliedEthPrice > 0 && ethPriceUsd > 0 ? impliedEthPrice * ethPriceUsd : 0)
  );
  const price = toNumber(trade?.price ?? impliedUsdPrice);

  return {
    _id: trade?._id ?? trade?.id,
    createdBy: trade?.createdBy,
    time: timestamp,
    timestamp,
    tokenAmount,
    ethAmount,
    lamportAmount: toNumber(trade?.lamportAmount ?? trade?.ethAmount ?? ethAmount),
    swapDirection: String(trade?.swapDirection ?? trade?.action ?? "").toUpperCase(),
    price,
    impliedEthPrice,
    impliedUsdPrice,
    ethPriceUsd,
    progressMcap: toNumber(trade?.progressMcap),
    marketCapEth: toNumber(trade?.marketCapEth),
    marketCapUsd: toNumber(trade?.marketCapUsd ?? toNumber(trade?.marketCapEth) * ethPriceUsd),
    normalizedMarketCapEth: toNumber(trade?.normalizedMarketCapEth),
    normalizedMarketCapUsd: toNumber(trade?.normalizedMarketCapUsd),
    ethRaised: toNumber(trade?.ethRaised),
    targetRaiseEth: toNumber(trade?.targetRaiseEth),
    targetMarketCapEth: toNumber(trade?.targetMarketCapEth),
    targetMarketCapUsd: toNumber(trade?.targetMarketCapUsd),
    tx: trade?.tx ?? trade?.transactionHash ?? "",
  };
};

const mapLatestTrade = (trade: any): LatestTradeType => {
  const mapped = mapTradeRecord(trade);
  return {
    token: trade?.token ?? trade?.mint ?? "",
    name: trade?.name ?? "",
    ticker: trade?.ticker ?? "",
    creator: trade?.creator ?? trade?.creatorAddress ?? "",
    creatorDisplayName: trade?.creatorDisplayName ?? trade?.creator,
    avatar: trade?.avatar ?? trade?.url ?? "",
    url: trade?.url ?? "",
    progressMcap: mapped.progressMcap,
    marketCapEth: mapped.marketCapEth,
    marketCapUsd: mapped.marketCapUsd,
    normalizedMarketCapEth: mapped.normalizedMarketCapEth,
    normalizedMarketCapUsd: mapped.normalizedMarketCapUsd,
    reply: toNumber(trade?.reply),
    action: mapped.swapDirection,
    amount: mapped.ethAmount ?? 0,
    ethAmount: mapped.ethAmount,
    lamportAmount: mapped.lamportAmount,
    tokenAmount: mapped.tokenAmount,
    price: mapped.price,
    impliedEthPrice: mapped.impliedEthPrice,
    impliedUsdPrice: mapped.impliedUsdPrice,
    ethPriceUsd: mapped.ethPriceUsd,
    ethRaised: mapped.ethRaised,
    targetRaiseEth: mapped.targetRaiseEth,
    targetMarketCapEth: mapped.targetMarketCapEth,
    targetMarketCapUsd: mapped.targetMarketCapUsd,
    timestamp: mapped.timestamp ?? mapped.time,
    tx: mapped.tx,
  };
};

export const signIn = async (wallet: string) => {
  try {
    const response = await axios.post('/user/sign-in', { wallet });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error("Sign-in failed:", error.response?.data || error.message);
      throw new Error(error.response?.data?.message || "Sign-in failed");
    }
    console.error("Unexpected error during sign-in:", error);
    throw new Error("An unexpected error occurred");
  }
};

export const signUp = async (data: any) => {
  try {
    const response = await axios.post('/user/sign-up', data);
    return response.data;
  } catch (error) {
    console.log(error);
  }
};

export const fetchUserInfo = async (userId: string) => {
  try {
    const response = await axios.get(`/user/${userId}`);
    return response.data;
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const updateUserProfile = async (data: userType) => {
  try {
    const response = await axios.post('/user/updateProfile', data);
    return response;
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const fetchCoins = async (): Promise<TokenType[]> => {
  try {
    const response = await axios.get('/coin');
    return Array.isArray(response.data) ? response.data.map(mapCoinResponse) : [];
  } catch (error) {
    console.log(error);
    return [];
  }
};

export const fetchCoin = async (contractAddress: string): Promise<TokenType | null> => {
  try {
    const response = await axios.get(`/coin/${contractAddress}`);
    return response.data ? mapCoinResponse(response.data) : null;
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const fetchTradesStatus = async (contractAddress: string): Promise<TokenTradeType[]> => {
  try {
    const response = await axios.get(`/tokenTrade/${contractAddress}`);
    const records = Array.isArray(response.data) ? response.data : [];
    return records.map(mapTradeRecord);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error("Failed to fetch trades:", error.response?.data || error.message);
    } else {
      console.error("Failed to fetch trades:", error);
    }
    return [];
  }
};

export const saveToken = async (data: {
  tokenAddress: string;
  description: string;
  image: string;
  website?: string;
  twitter?: string;
  telegram?: string;
}) => {
  try {
    const response = await axios.post(`/coin/create/`, {
      tokenAddress: data.tokenAddress,
      description: data.description,
      url: data.image,
      website: data.website,
      twitter: data.twitter,
      telegram: data.telegram,
    });
    return response.data;
  } catch (error) {
    console.error("Failed to save token metadata:", error);
    throw error;
  }
};

export const getChartTable = async (token: string, range: number, countBack: number) => {
  try {
    const response = await axios.post(`/chart/${token}`, { range, countBack });
    return response.data;
  } catch (error) {
    console.error("Failed to fetch chart data:", error);
    throw error;
  }
};

const localCommentStorage = {
  getComments: (tokenAddress: string): CommentType[] => {
    try {
      const storageKey = `comments_${tokenAddress}`;
      const storedComments = localStorage.getItem(storageKey);
      return storedComments ? JSON.parse(storedComments) : [];
    } catch (error) {
      console.error("Failed to read comments from local storage:", error);
      return [];
    }
  },
  saveComments: (tokenAddress: string, comments: CommentType[]): void => {
    try {
      const storageKey = `comments_${tokenAddress}`;
      localStorage.setItem(storageKey, JSON.stringify(comments));
    } catch (error) {
      console.error("Failed to persist comments locally:", error);
    }
  },
  addComment: (tokenAddress: string, comment: CommentType): CommentType => {
    const comments = localCommentStorage.getComments(tokenAddress);
    comments.unshift(comment);
    localCommentStorage.saveComments(tokenAddress, comments);
    return comment;
  },
  likeComment: (tokenAddress: string, commentId: string, userWallet: string) => {
    const comments = localCommentStorage.getComments(tokenAddress);
    const updated = comments.map((comment) => {
      if (comment.id === commentId && !comment.likedBy.includes(userWallet)) {
        comment.likes += 1;
        comment.likedBy.push(userWallet);
      }
      return comment;
    });
    localCommentStorage.saveComments(tokenAddress, updated);
    const target = updated.find((comment) => comment.id === commentId);
    return {
      success: true,
      data: {
        likes: target?.likes ?? 0,
        isLiked: true,
      },
    };
  },
  unlikeComment: (tokenAddress: string, commentId: string, userWallet: string) => {
    const comments = localCommentStorage.getComments(tokenAddress);
    const updated = comments.map((comment) => {
      if (comment.id === commentId && comment.likedBy.includes(userWallet)) {
        comment.likes = Math.max(0, comment.likes - 1);
        comment.likedBy = comment.likedBy.filter((wallet) => wallet !== userWallet);
      }
      return comment;
    });
    localCommentStorage.saveComments(tokenAddress, updated);
    const target = updated.find((comment) => comment.id === commentId);
    return {
      success: true,
      data: {
        likes: target?.likes ?? 0,
        isLiked: false,
      },
    };
  },
};

const normalizeComment = (comment: any): CommentType => {
  if (!comment || typeof comment !== "object") {
    return {
      id: crypto.randomUUID(),
      content: "",
      tokenAddress: "",
      createdBy: "",
      timestamp: Date.now(),
      likes: 0,
      likedBy: [],
      images: [],
      files: [],
    };
  }

  return {
    id: comment.id ?? comment._id ?? crypto.randomUUID(),
    content: comment.content ?? "",
    tokenAddress: comment.tokenAddress ?? "",
    createdBy: comment.createdBy ?? "",
    parentId: comment.parentId ?? undefined,
    timestamp: typeof comment.timestamp === "number" ? comment.timestamp : Date.now(),
    likes: typeof comment.likes === "number" ? comment.likes : 0,
    likedBy: Array.isArray(comment.likedBy) ? comment.likedBy : [],
    images: Array.isArray(comment.images) ? comment.images : [],
    files: Array.isArray(comment.files) ? comment.files : [],
  };
};

export const fetchComments = async (tokenAddress: string): Promise<CommentType[]> => {
  try {
    const response = await axios.get(`/comment/${tokenAddress}`);
    const payload = Array.isArray(response.data?.data) ? response.data.data : [];
    return payload.map(normalizeComment);
  } catch (error) {
    console.error("Error fetching comments:", error);
    return localCommentStorage.getComments(tokenAddress).map(normalizeComment);
  }
};

export const postComment = async (data: {
  tokenAddress: string;
  content: string;
  userWallet: string;
  parentId?: string;
  images?: string[];
  files?: { name: string; url: string }[];
}) => {
  try {
    const response = await axios.post('/comment', data);
    return normalizeComment(response.data?.data);
  } catch (error) {
    console.error("Falling back to local comment storage:", error);
    const newComment: CommentType = {
      id: Math.random().toString(36).slice(2),
      content: data.content,
      tokenAddress: data.tokenAddress,
      createdBy: data.userWallet,
      parentId: data.parentId,
      timestamp: Date.now(),
      likes: 0,
      likedBy: [],
      images: data.images ?? [],
      files: data.files ?? [],
    };
    return normalizeComment(localCommentStorage.addComment(data.tokenAddress, newComment));
  }
};

export const likeComment = async (commentId: string, userWallet: string) => {
  try {
    const response = await axios.post(`/comment/${commentId}/like`, { userWallet });
    return response.data;
  } catch (error) {
    console.error("Falling back to local like update:", error);
    const keys = Object.keys(localStorage).filter((key) => key.startsWith('comments_'));
    for (const key of keys) {
      const tokenAddress = key.replace('comments_', '');
      const result = localCommentStorage.likeComment(tokenAddress, commentId, userWallet);
      if (result) {
        return result;
      }
    }
    throw error;
  }
};

export const unlikeComment = async (commentId: string, userWallet: string) => {
  try {
    const response = await axios.post(`/comment/${commentId}/unlike`, { userWallet });
    return response.data;
  } catch (error) {
    console.error("Falling back to local unlike update:", error);
    const keys = Object.keys(localStorage).filter((key) => key.startsWith('comments_'));
    for (const key of keys) {
      const tokenAddress = key.replace('comments_', '');
      const result = localCommentStorage.unlikeComment(tokenAddress, commentId, userWallet);
      if (result) {
        return result;
      }
    }
    throw error;
  }
};

export const fetchLastTrade = async (): Promise<LatestTradeType | null> => {
  try {
    const response = await axios.get('/tokenTrade/lastrade');
    if (response.data) {
      return mapLatestTrade(response.data);
    }
    return null;
  } catch (error) {
    console.error("Error fetching last trade:", error);
    return null;
  }
};

export const fetchTokenHolders = async (contractAddress: string) => {
  try {
    const response = await axios.get(`/holder/${contractAddress}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching token holders:", error);
    throw error;
  }
};

export const fetchCurrentPepePrice = async (): Promise<number | null> => {
  try {
    const response = await axios.get('/price-service/latest');
    if (response.data?.success) {
      const priceRecord = Array.isArray(response.data.data) ? response.data.data[0] : undefined;
      return typeof priceRecord?.Price === 'number' ? priceRecord.Price : null;
    }
    return null;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error('Failed to fetch current PEPE price:', error.response?.data || error.message);
    } else {
      console.error('Failed to fetch current PEPE price:', error);
    }
    return null;
  }
};

export const getPriceServiceStatus = async (): Promise<{ isRunning: boolean } | null> => {
  try {
    const response = await axios.get('/price-service/status');
    if (response.data?.success) {
      return response.data.data;
    }
    return null;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error('Failed to get price service status:', error.response?.data || error.message);
    } else {
      console.error('Failed to get price service status:', error);
    }
    return null;
  }
};

export const triggerPriceFetch = async (): Promise<boolean> => {
  try {
    const response = await axios.post('/price-service/fetch-once');
    return Boolean(response.data?.success);
  } catch (error) {
    console.error('Error triggering price fetch:', error);
    return false;
  }
};

export const testBackendConnection = async (): Promise<boolean> => {
  try {
    await axios.get('/price-service/status');
    await axios.get('/price-service/latest');
    return true;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error('Backend connection test failed:', error.response?.data || error.message);
    } else {
      console.error('Backend connection test failed:', error);
    }
    return false;
  }
};

type WatchlistEntry = { userWallet: string; token: string; createdAt?: string };

export const fetchTokenWatchers = async (walletAddress: string): Promise<{ watchlist: WatchlistEntry[] }> => {
  try {
    const response = await axios.get(`/watchlist/${walletAddress}`);
    const watchlist = Array.isArray(response.data?.watchlist) ? response.data.watchlist : [];
    return { watchlist };
  } catch (error) {
    console.error('Error fetching token watchers:', error);
    return { watchlist: [] };
  }
};

export const addTokenWatcher = async (contractAddress: string, walletAddress: string) => {
  try {
    const response = await axios.post(`/watchlist/add`, {
      user: walletAddress,
      coin: contractAddress,
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error('Error adding token watcher:', error.response?.data || error.message);
    } else {
      console.error('Error adding token watcher:', error);
    }
    throw error;
  }
};

export const removeTokenWatcher = async (contractAddress: string, walletAddress: string) => {
  try {
    const response = await axios.post(`/watchlist/remove`, {
      user: walletAddress,
      coin: contractAddress,
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error('Error removing token watcher:', error.response?.data || error.message);
    } else {
      console.error('Error removing token watcher:', error);
    }
    throw error;
  }
};
